import { toLocalizedFormat, calculateLineItemTotal, calculateInvoiceTotals, moneyUtils } from '@dutchinvoice/shared-utils';
// Format money for display with locale-specific formatting
const formatMoney = (amount, language) => {
    const numericAmount = parseFloat(amount.toString()) || 0;
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : 'en-US', {
        style: 'currency',
        currency: 'EUR',
    }).format(numericAmount);
};
const getEnabledPaymentMethods = (paymentMethods) => {
    var _a, _b, _c, _d;
    const methods = [];
    if ((_a = paymentMethods.bank) === null || _a === void 0 ? void 0 : _a.enabled)
        methods.push('bank');
    if ((_b = paymentMethods.paypal) === null || _b === void 0 ? void 0 : _b.enabled)
        methods.push('paypal');
    if ((_c = paymentMethods.mollie) === null || _c === void 0 ? void 0 : _c.enabled)
        methods.push('mollie');
    if ((_d = paymentMethods.stripe) === null || _d === void 0 ? void 0 : _d.enabled)
        methods.push('stripe');
    return methods;
};
export const createInvoiceEmailTemplate = ({ previewText, customMessage, invoice, language, t, logo, paymentBaseUrl = '', siteUrl = '', isPreview = false, isReminder = false, }) => {
    var _a, _b;
    // Calculate totals for each item with the utility function
    const items = invoice.items.map(item => {
        const total = calculateLineItemTotal(item);
        const vatRate = parseFloat(item.vatRate) || 0;
        const vatAmount = moneyUtils.multiply(total, vatRate / 100);
        return Object.assign(Object.assign({}, item), { total,
            vatAmount });
    });
    // Use the centralized calculation utility
    const { subtotal, discountAmount, vat: vatTotal, total } = calculateInvoiceTotals(invoice.items, invoice.discount || { type: 'none', value: '0' });
    const messageBody = customMessage || (isReminder
        ? t('email.reminderDefaultMessage', {
            company: invoice.sender.company,
            invoiceNumber: invoice.invoiceNumber
        })
        : t('email.defaultMessage', {
            company: invoice.sender.company,
            invoiceNumber: invoice.invoiceNumber
        }));
    const fullMessage = messageBody;
    // Get payment terms days
    const paymentTermsDays = invoice.paymentTerms ? parseInt(invoice.paymentTerms, 10) : 14;
    // Get enabled payment methods
    const enabledPaymentMethods = invoice.paymentMethods ? getEnabledPaymentMethods(invoice.paymentMethods) : [];
    // Derive frontend URL from payment base URL (remove 'api.' prefix)
    const frontendBaseUrl = paymentBaseUrl.replace('api.', '');
    const localizedDate = toLocalizedFormat(invoice.date, language);
    const localizedDueDate = toLocalizedFormat(invoice.dueDate, language);
    // Create optimized preview text for better open rates - use the translations directly
    const defaultPreviewText = isReminder ? t('email.reminderSubject', {
        invoiceNumber: invoice.invoiceNumber,
        company: invoice.sender.company
    }) : t('email.previewText');
    const optimizedPreviewText = !previewText || previewText === defaultPreviewText
        ? (isReminder
            ? t('email.reminderSubject', {
                invoiceNumber: invoice.invoiceNumber,
                company: invoice.sender.company
            })
            : t('email.previewText', {
                invoiceNumber: invoice.invoiceNumber,
                company: invoice.sender.company
            }))
        : previewText;
    return `
    <mjml>
      <mj-head>
        <!-- Removed mj-title completely as it's causing issues in some email clients -->
        <mj-font name="Arial" href="https://fonts.googleapis.com/css2?family=Arial&display=swap" />
        <mj-attributes>
          <mj-all font-family="Arial, 'Helvetica Neue', Helvetica, sans-serif" background-color="#f8fafc" />
          <mj-section padding="0" background-color="#f8fafc" />
          <mj-column padding="0" background-color="#f8fafc" />
          <mj-text padding="0" />
        </mj-attributes>
        <!--[if gte mso 9]>
        <xml>
          <o:OfficeDocumentSettings>
            <o:AllowPNG/>
            <o:PixelsPerInch>96</o:PixelsPerInch>
          </o:OfficeDocumentSettings>
        </xml>
        <![endif]-->
      </mj-head>
      <mj-body width="100%" background-color="#f8fafc">
        <!-- Hidden preheader text with multiple hiding techniques for maximum compatibility -->
        <mj-section padding="0">
          <mj-column padding="0">
            <mj-text css-class="preheader" color="#f8fafc" font-size="0px" line-height="0px" padding="0" font-weight="normal" text-decoration="none">
              <div style="display:none; font-size:1px; color:#f8fafc; line-height:1px; max-height:0px; max-width:0px; opacity:0; overflow:hidden; mso-hide:all;">
                ${optimizedPreviewText}
                &nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;
              </div>
            </mj-text>
          </mj-column>
        </mj-section>
        
        <!--[if mso]>
        <table cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="#f8fafc" style="background-color:#f8fafc;">
        <tr>
        <td>
        <![endif]-->
        <mj-wrapper padding="0" background-color="#f8fafc">
          <!-- Simple table-based fixed width container -->
          <mj-section>
            <mj-column>
              <!-- START LIQUID WRAPPER -->
              <table cellpadding="0" cellspacing="0" border="0" style="padding:0px;margin:0px;width:100%; background-color:#f8fafc;">
                <tr>
                  <td style="padding:0px;margin:0px; background-color:#f8fafc;">&nbsp;</td>
                  <td style="padding:0px;margin:0px;background-color:#f8fafc;" width="640">
                    ${isReminder ? `
                    <!-- Reminder Banner -->
                    <div style="background-color:#fef3c7; border-left:4px solid #f59e0b; padding:16px 20px; margin:0 0 20px 0; border-radius:8px;">
                      <p style="margin:0; font-size:16px; font-weight:600; color:#92400e;">⏰ ${t('email.reminderHeader')}</p>
                    </div>
                    ` : ''}
                    <!-- Email Message -->
                    <div style="padding:20px; font-size:16px; line-height:1.6; color:#334155;">
                ${(() => {
        var _a;
        const recipientName = ((_a = invoice.recipient) === null || _a === void 0 ? void 0 : _a.contactName) && invoice.recipient.contactName.trim()
            ? invoice.recipient.contactName
            : invoice.recipient.company;
        return `${t('email.greeting')} ${recipientName},<br /><br />${fullMessage.replace(/\n/g, '<br />')}`;
    })()}
              </div>
                    <div style="border-top:1px solid #e2e8f0; margin:0 20px;"></div>

        <!-- Invoice Content -->
                    <div style="padding:20px;">
                      <table cellpadding="0" cellspacing="0" border="0" style="width:100%; margin:0 auto; background-color:#ffffff; border-radius:24px; border:1px solid #e2e8f0; max-width:600px;">
                        <tr>
                          <td style="padding:30px 30px 15px;">
                            ${(logo === null || logo === void 0 ? void 0 : logo.url) ? `
                            <div style="text-align:center; margin-bottom:24px;">
                              <img src="${logo.url}" alt="${invoice.sender.company} logo" style="max-width: 200px; max-height:96px; object-fit:contain;" />
                            </div>
                            ` : ''}
                            <!-- Invoice Info -->
                            <table cellpadding="0" cellspacing="0" style="width:100%;">
                              <tr>
                                <td style="width:50%; vertical-align:top;">
                                  <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 4px;">${t('invoice.number')}</p>
                                  <p style="font-size:20px; color:#0f172a; font-weight:bold; margin:0;">${invoice.invoiceNumber}</p>
                                </td>
                                <td style="width:50%; vertical-align:top; text-align:right;">
                                  <div style="margin-bottom:12px;">
                                    <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 4px;">${t('invoice.date')}</p>
                                    <p style="font-size:14px; color:#1e293b; margin:0;">${localizedDate}</p>
                      </div>
                      <div>
                                    <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 4px;">${t('invoice.dueDate')}</p>
                                    <p style="font-size:14px; color:#1e293b; margin:0;">${localizedDueDate}</p>
                      </div>
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                        
                        <tr>
                          <td style="padding:15px 30px 25px;">
                            <!-- Sender/Recipient -->
                            <table cellpadding="0" cellspacing="0" style="width:100%;">
                              <tr>
                                <td style="width:48%; vertical-align:top; padding-right:4%;">
                                  <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 8px;">${t('invoice.to')}</p>
                                  <p style="font-size:15px; color:#0f172a; font-weight:bold; margin:0 0 8px;">${invoice.recipient.company}</p>
                                  ${((_a = invoice.recipient) === null || _a === void 0 ? void 0 : _a.contactName) ? `<p style="font-size:14px; color:#475569; margin:0 0 4px;">t.a.v. ${invoice.recipient.contactName}</p>` : ''}
                                  <p style="font-size:14px; color:#475569; margin:0 0 4px;">${invoice.recipient.address}</p>
                                  <p style="font-size:14px; color:#475569; margin:0 0 4px;">${invoice.recipient.postalCode} ${invoice.recipient.city}</p>
                                  ${((_b = invoice.recipient) === null || _b === void 0 ? void 0 : _b.additionalInfo) ? `<p style="font-size:14px; color:#475569; margin:0 0 4px;">${invoice.recipient.additionalInfo}</p>` : ''}
                    </td>
                                <td style="width:48%; vertical-align:top;">
                                  <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 8px;">${t('invoice.from')}</p>
                                  <p style="font-size:15px; color:#0f172a; font-weight:bold; margin:0 0 8px;">${invoice.sender.company}</p>
                                  <p style="font-size:14px; color:#475569; margin:0 0 4px;">${invoice.sender.address}</p>
                                  <p style="font-size:14px; color:#475569; margin:0 0 4px;">${invoice.sender.postalCode} ${invoice.sender.city}</p>
                                  <div style="margin-top:16px; border-top:1px solid #e2e8f0; padding-top:16px;">
                                    <table cellpadding="0" cellspacing="0" style="width:100%;">
                                      <tr>
                                        <td style="width:60%; vertical-align:top; padding-right:10px;">
                                          <div style="margin-bottom:8px;">
                                            <p style="font-size:12px; color:#94a3b8; margin:0 0 2px;">${t('invoice.vat')}</p>
                                            <p style="font-size:14px; color:#1e293b; margin:0;">${invoice.sender.vatNumber}</p>
                              </div>
                            </td>
                                        <td style="width:40%; vertical-align:top;">
                                          <div style="margin-bottom:8px;">
                                            <p style="font-size:12px; color:#94a3b8; margin:0 0 2px;">${t('invoice.kvk')}</p>
                                            <p style="font-size:14px; color:#1e293b; margin:0;">${invoice.sender.kvkNumber}</p>
                              </div>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </td>
                  </tr>
                </table>
                          </td>
                        </tr>

                        <tr>
                          <td style="padding:0 30px 20px;">
              <!-- Line Items -->
                            <table cellpadding="0" cellspacing="0" style="width:100%; border-collapse:collapse; margin-bottom:20px;">
                  <thead>
                    <tr>
                                  <th style="text-align:right; padding-right:12px; width:50px; font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; font-weight:normal;">${t('invoice.lineItems.quantity')}</th>
                                  <th style="text-align:left; width:40%; font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; font-weight:normal;">${t('invoice.lineItems.description')}</th>
                                  <th style="text-align:right; padding-right:12px; width:110px; font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; font-weight:normal;">${t('invoice.lineItems.price')}</th>
                                  <th style="text-align:right; padding-right:12px; width:60px; font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; font-weight:normal;">${t('invoice.lineItems.vatRate')}</th>
                                  <th style="text-align:right; width:120px; font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; font-weight:normal;">${t('invoice.lineItems.amount')}</th>
                    </tr>
                  </thead>
                  <tbody style=" font-size:13.5px;">
                    ${items.map(item => `
                      <tr>
                                    <td style="text-align:right; padding:8px 12px 8px 0;">${item.quantity}</td>
                                    <td style="word-break:break-word; padding:8px 0;">${item.description}</td>
                                    <td style="text-align:right; padding:8px 12px 8px 0;">${formatMoney(item.price, language)}</td>
                                    <td style="text-align:right; padding:8px 12px 8px 0;">${item.vatRate}%</td>
                                    <td style="text-align:right; padding:8px 0;">${formatMoney(item.total, language)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
                          </td>
                        </tr>

                        <tr>
                          <td style="padding:0 30px 30px;">
              <!-- Totals -->
                            <table cellpadding="0" cellspacing="0" style="width:100%; border-collapse:collapse;">
                              <tr>
                                <td style="width:100%;"></td>
                                <td style="white-space:nowrap; color:#64748b; padding:4px 24px 4px 0; font-size:14px; text-align:right;">${t('invoice.subtotal')}</td>
                                <td style="white-space:nowrap; text-align:right; color:#1e293b; min-width:120px; font-size:14px; padding:4px 0;">${formatMoney(subtotal, language)}</td>
                  </tr>
                  ${parseFloat(discountAmount) > 0 ? `
                    <tr>
                                  <td style="width:100%;"></td>
                                  <td style="white-space:nowrap; color:#64748b; padding:4px 24px 4px 0; font-size:14px; text-align:right;">${t('invoice.discount.label')}</td>
                                  <td style="white-space:nowrap; text-align:right; color:#1e293b; min-width:120px; font-size:14px; padding:4px 0;">-${formatMoney(discountAmount, language)}</td>
                    </tr>
                  ` : ''}
                  <tr>
                                <td style="width:100%;"></td>
                                <td style="white-space:nowrap; color:#64748b; padding:4px 24px 4px 0; font-size:14px; text-align:right;">${t('invoice.vat')}</td>
                                <td style="white-space:nowrap; text-align:right; color:#1e293b; min-width:120px; font-size:14px; padding:4px 0;">${formatMoney(vatTotal, language)}</td>
                  </tr>
                  <tr>
                                <td style="width:100%;"></td>
                                <td style="white-space:nowrap; color:#0f172a; font-weight:bold; padding:12px 24px 4px 0; font-size:15px; text-align:right;">${t('invoice.total')}</td>
                                <td style="white-space:nowrap; text-align:right; color:#0f172a; font-weight:bold; min-width:120px; font-size:15px; padding:12px 0 4px 0;">${formatMoney(total, language)}</td>
                  </tr>
                </table>
                          </td>
                        </tr>
                        
                        ${enabledPaymentMethods.length > 0 ? `
                          <tr>
                            <td style="border-top:1px solid #e2e8f0; padding:30px">
                              ${(() => {
        const methods = [];
        enabledPaymentMethods.forEach((methodType, index) => {
            var _a, _b, _c, _d;
            const isFirstMethod = index === 0;
            const isLastMethod = index === enabledPaymentMethods.length - 1;
            const hasMultipleMethods = enabledPaymentMethods.length > 1;
            const hasOnlyOnlinePayments = enabledPaymentMethods.length > 0 && !enabledPaymentMethods.includes('bank');
            const showMethodInButton = (enabledPaymentMethods.length > 1 && !enabledPaymentMethods.includes('bank')) ||
                (enabledPaymentMethods.length > 2 && enabledPaymentMethods.includes('bank'));
            if (methodType === 'bank' && ((_a = invoice.paymentMethods) === null || _a === void 0 ? void 0 : _a.bank)) {
                methods.push(`
                                      <div style="margin-bottom:${isLastMethod ? '0' : '24px'};">
                                        <p style="font-size:14px; color:#475569; margin:0 0 16px;">
                                          ${t('invoice.paymentMethods.bank.paymentInstructionWithDays', { days: paymentTermsDays })}
                                        </p>
                                        <table cellpadding="0" cellspacing="0" border="0" style="width:100%;">
                                          <tr>
                                            <td style="padding-bottom:16px; padding-right:24px; min-width:160px; vertical-align:top;">
                                              <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 6px;">
                                                ${t('invoice.paymentMethods.bank.accountName')}
                                              </p>
                                              <p style="font-size:14px; color:#1e293b; font-weight:bold; margin:0;">
                                                ${invoice.paymentMethods.bank.accountName}
                                              </p>
                                            </td>
                                            <td style="padding-bottom:16px; min-width:200px; vertical-align:top;">
                                              <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 6px;">
                                                ${t('invoice.paymentMethods.bank.iban')}
                                              </p>
                                              <p style="font-size:14px; color:#1e293b; font-family:Arial, monospace; letter-spacing:0.5px; margin:0;">
                                                ${invoice.paymentMethods.bank.iban}
                                              </p>
                                            </td>
                                          </tr>
                                          <tr>
                                            ${invoice.paymentMethods.bank.bic && invoice.paymentMethods.bank.bic.trim() !== '' ? `
                                              <td style="padding-right:24px; min-width:110px; vertical-align:top;">
                                                <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 6px;">
                                                  BIC
                                                </p>
                                                <p style="font-size:14px; color:#1e293b; font-family:Arial, monospace; letter-spacing:0.5px; margin:0;">
                                                  ${invoice.paymentMethods.bank.bic}
                                                </p>
                                              </td>
                                            ` : '<td></td>'}
                                            <td style="min-width:140px; vertical-align:top;">
                                              <p style="font-size:12px; color:#94a3b8; text-transform:uppercase; letter-spacing:0.3px; margin:0 0 6px;">
                                                ${t('invoice.paymentMethods.bank.reference')}
                                              </p>
                                              <p style="font-size:14px; color:#1e293b; font-weight:bold; margin:0;">
                                                ${invoice.invoiceNumber}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </div>
                                    `);
            }
            else if (methodType === 'paypal' && ((_b = invoice.paymentMethods) === null || _b === void 0 ? void 0 : _b.paypal)) {
                methods.push(`
                                      <div style="margin-bottom:${isLastMethod ? '0' : '24px'};">
                                        <p style="font-size:14px; color:#475569; margin:0 0 16px;">
                                          ${hasOnlyOnlinePayments && isFirstMethod
                    ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                    : hasMultipleMethods && !isFirstMethod
                        ? t('invoice.paymentMethods.alternative')
                        : t('invoice.paymentMethods.online')}
                                        </p>
                                        <table cellpadding="0" cellspacing="0" border="0">
                                          <tr>
                                            <td style="background-color:#0070f3; border-radius:6px; padding:10px 20px; text-align:center; min-width:200px;">
                                              <p style="color:#ffffff; font-weight:bold; margin:0; font-size:14px;">
                                                ${showMethodInButton
                    ? `${t('invoice.paymentMethods.payNow')} - PayPal`
                    : t('invoice.paymentMethods.payNow')}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </div>
                                    `);
            }
            else if (methodType === 'mollie' && ((_c = invoice.paymentMethods) === null || _c === void 0 ? void 0 : _c.mollie)) {
                methods.push(`
                                      <div style="margin-bottom:${isLastMethod ? '0' : '24px'};">
                                        <p style="font-size:14px; color:#475569; margin:0 0 16px;">
                                          ${hasOnlyOnlinePayments && isFirstMethod
                    ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                    : hasMultipleMethods && !isFirstMethod
                        ? t('invoice.paymentMethods.alternative')
                        : t('invoice.paymentMethods.online')}
                                        </p>
                                        <table cellpadding="0" cellspacing="0" border="0">
                                          <tr>
                                            <td style="background-color:#0070f3; border-radius:6px; padding:10px 20px; text-align:center; min-width:200px;">
                                              <a href="${paymentBaseUrl}/invoice/${invoice.id}" style="text-decoration:none; color:#ffffff;">
                                                <p style="color:#ffffff; font-weight:bold; margin:0; font-size:14px;">
                                                  ${showMethodInButton
                    ? `${t('invoice.paymentMethods.payNow')} - Mollie`
                    : t('invoice.paymentMethods.payNow')}
                                                </p>
                                              </a>
                                            </td>
                                          </tr>
                                        </table>
                                      </div>
                                    `);
            }
            else if (methodType === 'stripe' && ((_d = invoice.paymentMethods) === null || _d === void 0 ? void 0 : _d.stripe)) {
                methods.push(`
                                      <div style="margin-bottom:${isLastMethod ? '0' : '24px'};">
                                        <p style="font-size:14px; color:#475569; margin:0 0 16px;">
                                          ${hasOnlyOnlinePayments && isFirstMethod
                    ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                    : hasMultipleMethods && !isFirstMethod
                        ? t('invoice.paymentMethods.alternative')
                        : t('invoice.paymentMethods.online')}
                                        </p>
                                        <table cellpadding="0" cellspacing="0" border="0">
                                          <tr>
                                            <td style="background-color:#0070f3; border-radius:6px; padding:10px 20px; text-align:center; min-width:200px;">
                                              <p style="color:#ffffff; font-weight:bold; margin:0; font-size:14px;">
                                                ${showMethodInButton
                    ? `${t('invoice.paymentMethods.payNow')} - Stripe`
                    : t('invoice.paymentMethods.payNow')}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </div>
                                    `);
            }
        });
        return methods.join('');
    })()}
                            </td>
                          </tr>
                        ` : ''}
                        
              ${invoice.notes ? `
                          <tr>
                            <td style="border-top:1px solid #e2e8f0; padding:30px; color:#475569; font-size:14px;">
                  ${invoice.notes}
                            </td>
                          </tr>
              ` : ''}
                      </table>
                    </div>
                    
                    <!-- Not sure if you paid - check here -->
                    ${!isPreview ? `
                    <div style="padding:0 30px 20px; background-color:#f8fafc; text-align:left; font-size:12px; color:#94a3b8;">
                      <p style="margin:0;">
                        <a href="${siteUrl}/invoice-status/${invoice.id}" style="color:#0070f3; text-decoration:none;">
                          ${t('invoice.paymentMethods.notSure')}
                        </a>
                      </p>
                    </div>
                    ` : ''}
                    
                    <div style="padding:10px 0 30px; background-color:#f8fafc; text-align:center; font-size:12px; color:#94a3b8;">
                      ${invoice.sender.company}<br />
                      ${invoice.sender.address}<br />
                      ${invoice.sender.postalCode} ${invoice.sender.city}
                    </div>
                  </td>
                  <td style="padding:0px;margin:0px;">&nbsp;</td>
                </tr>
              </table>
              <!-- END LIQUID WRAPPER -->
            </mj-column>
          </mj-section>
        </mj-wrapper>
        <!--[if mso]>
        </td>
        </tr>
        </table>
        <![endif]-->
      </mj-body>
    </mjml>
  `;
};
